import {
  type CustomerQuote,
  type DateOnly,
  DateOnlyClass,
  type FixingIndexMarkMessageData,
  getQuoteLegQuantity,
  getSpreadPct,
  isPricingModeRelative,
  logger,
  type MinimalSubscriptionResponse,
  PricingModeEnum,
  useBehaviorSubject,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import { useAppStateSelector } from '../../../../providers/AppStateProvider';
import { useIndexMarksOnDate } from '../../../../utils/indexMarkUtils';
import { useManualRFQPricingContext } from '../ManualRFQPricingContextProvider';
import { getSizeCounteredLegQty } from '../sizeCountering';
import type { CompositeBidAskFields, LegBidAskFields } from '../types';
import type { PricingRow } from './types';

interface RFQPricingSliceToPricingRowsParams {
  quote: CustomerQuote | undefined;
  compositeBidAsk: CompositeBidAskFields;
  legBidAsks: LegBidAskFields[];
  indexMarksByName: Map<string, FixingIndexMarkMessageData>;
}

/** Given the current quote and legBidAsks from the pricing slice, returns the PricingRow array for use with the pricing blotter */
function rfqPricingSliceToPricingRows({
  quote,
  compositeBidAsk,
  legBidAsks,
  indexMarksByName,
}: RFQPricingSliceToPricingRowsParams): PricingRow[] {
  if (!quote) {
    return [];
  }

  const rows = legBidAsks.map(leg => {
    const legSummary = quote?.LegSummary?.find(ls => ls.Symbol === leg.symbol);
    if (!legSummary) {
      // legBidAsks are built from LegSummaries, shouldn't happen. Just raising an error if it happens and calling it a day.
      logger.error(new Error('rfqPricingSliceToPricingRows: Could not find LegSummary for LegBidAsk'), {
        extra: {
          quote,
          legSymbol: leg.symbol,
          legBidAskSymbols: legBidAsks.map(l => l.symbol),
          legSummarySymbols: quote?.LegSummary?.map(l => l.Symbol),
        },
      });
      return undefined;
    }

    let spread: string | undefined = undefined;

    if (leg.bid.value && leg.ask.value) {
      spread = getSpreadPct(leg.bid.value, leg.ask.value, {
        isRelativePrice: isPricingModeRelative(quote.PricingMode),
      })?.toFixed();
    }

    // For legs, we use the composite bid-ask as "placeholder". We also compute a "placeholder spread" using these values here
    // Only applicable if we weren't able to compute a spread for the leg itself just above
    if (spread == null && compositeBidAsk.bid.value && compositeBidAsk.ask.value) {
      spread = getSpreadPct(compositeBidAsk.bid.value, compositeBidAsk.ask.value, {
        isRelativePrice: isPricingModeRelative(quote.PricingMode),
      })?.toFixed();
    }

    const indexMark = legSummary.FixingDetails?.Index
      ? indexMarksByName.get(legSummary.FixingDetails.Index)
      : undefined;
    return {
      ...legSummary,
      quantity: getQuoteLegQuantity(quote.Side, legSummary),
      bidQty: getSizeCounteredLegQty({
        topLevelQty: compositeBidAsk.bidQty.value,
        sizeRatio: legSummary.SizeRatio,
      }),
      offerQty: getSizeCounteredLegQty({
        topLevelQty: compositeBidAsk.askQty.value,
        sizeRatio: legSummary.SizeRatio,
      }),
      pricingMode: quote.PricingMode ?? PricingModeEnum.UnderlyingQuoteCurrency,
      amountCurrency: quote.AmountCurrency,
      bid: leg.bid,
      ask: leg.ask,
      bidPlaceholder: compositeBidAsk.bid.displayValue,
      askPlaceholder: compositeBidAsk.ask.displayValue,
      spread,
      indexMark,
    };
  });

  return compact(rows);
}

/** Grabs the state necessary from the pricing slice and builds a dataObservable from it to be passed to the per-leg pricing blotter */
export const usePricingRowsObs = () => {
  const { selectors } = useManualRFQPricingContext();
  const state = useAppStateSelector(selectors.selectState);

  const fixingDateMinusOne = useMemo<DateOnly | undefined>(() => {
    const fixingDate = state.currentCustomerQuote?.fixingDate;
    if (fixingDate == null) {
      return undefined;
    }

    return new DateOnlyClass(fixingDate).subDays(1) ?? undefined;
  }, [state.currentCustomerQuote?.fixingDate]);
  const indexMarksByName = useIndexMarksOnDate(fixingDateMinusOne);

  const rowParams = useMemo(
    () => ({
      quote: state.currentCustomerQuote,
      compositeBidAsk: state.form.compositeBidAsk,
      legBidAsks: state.form.legBidAsks,
      indexMarksByName,
    }),
    [indexMarksByName, state]
  );
  const { observable } = useBehaviorSubject<MinimalSubscriptionResponse<PricingRow>>(
    () => ({
      initial: true,
      data: rfqPricingSliceToPricingRows(rowParams),
    }),
    [rowParams]
  );

  return observable;
};
